import { cn } from "@/utils/cn.ts";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
} from "@babackapp/ui/components/dialog";
import useReturnRequestsOnce from "@babackapp/private-api-client/client/use-return-requests-once";
import { StoreIcon, PackageIcon } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";
import useAllViewerShops from "@babackapp/private-api-client/client/use-all-viewer-shops";
import { useSuperAdminAllShops } from "@babackapp/private-api-client/client/use-super-admin-all-shops";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";

const MAX_SHOPS_SHOWN = 50;

interface ShopCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ShopCommandPalette({
  isOpen,
  onClose,
}: ShopCommandPaletteProps) {
  const { data: ownShops } = useAllViewerShops();
  // A super admin is no member of the shops they support (BAB-3035): search
  // them all, loaded once the palette opens (cached, as in the switcher).
  const isSuperAdmin = useViewerIsSuperAdmin();
  const { data: allShops } = useSuperAdminAllShops(isSuperAdmin && isOpen);
  const shops = isSuperAdmin ? (allShops ?? ownShops) : ownShops;

  const t = useTranslation();
  const navigate = useNavigate();
  const { shopId: currentShopId } = useParams<{ shopId: string }>();
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

  // Debounce search value for API calls
  const debouncedSearch = useDebouncedCallback(
    (value: string) => {
      setDebouncedSearchValue(value);
    },
    400, // Same debounce as returns table search
  );

  useEffect(() => {
    debouncedSearch(searchValue);
  }, [searchValue, debouncedSearch]);

  // Reset search when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSearchValue("");
      setDebouncedSearchValue("");
    }
  }, [isOpen]);

  // Filter shops on name, Shopify domain or id (only if shops are loaded).
  // Capped: a super admin searches about a thousand of them.
  const needle = searchValue.trim().toLowerCase();
  const filteredShops = shops
    ? shops
        .filter((shop) =>
          [shop.name, shop.shopifyUrl ?? "", shop.id].some((value) =>
            value.toLowerCase().includes(needle),
          ),
        )
        .slice(0, MAX_SHOPS_SHOWN)
    : [];

  // Only search for returns if no shops match and we have a search value and
  // we're in a shop context, once the shops are loaded. A super admin may
  // have no shop of their own (BAB-3035): an empty list still counts as
  // loaded.
  const shouldSearchReturns =
    filteredShops.length === 0 &&
    debouncedSearchValue.trim().length > 0 &&
    !!currentShopId &&
    !!shops;

  // Search returns when no shops match - only call hook when needed
  const { data: returnRequestsData, isLoading: isLoadingReturns } =
    useReturnRequestsOnce({
      shopId: currentShopId || "", // Always provide shopId to avoid hook order issues
      search: shouldSearchReturns ? debouncedSearchValue : "", // Empty string will not trigger API call due to enabled: !!search?.length
      sortBy: "createdAt",
      sortOrder: "desc",
      page: 1,
    });

  // Don't render if data is not loaded
  if (!shops) {
    return null;
  }

  // Extract return requests from response - only when we should search returns
  const returnRequests = returnRequestsData?.data || [];

  // Limit return results for command palette
  const limitedReturns = returnRequests.slice(0, 10);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogOverlay className="fixed inset-0 z-50 bg-background/5 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:duration-150 data-[state=open]:duration-50" />
      <DialogContent
        showOverlay={false}
        className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] overflow-hidden p-0 border shadow-2xl bg-background rounded-xl backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:duration-150 data-[state=open]:duration-50"
      >
        <Command
          loop={true}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <CommandInput
            placeholder={t("returns.search")}
            value={searchValue}
            onValueChange={setSearchValue}
            className="border-none bg-transparent text-left"
          />
          <CommandList>
            <CommandEmpty>
              {shouldSearchReturns
                ? isLoadingReturns
                  ? t("sidebar.loading") // Don't show empty state while loading
                  : limitedReturns.length === 0
                    ? t("sidebar.noResult")
                    : null
                : t("sidebar.noResult")}
            </CommandEmpty>

            {filteredShops.length > 0 && (
              <CommandGroup heading={t("sidebar.shops")}>
                {filteredShops.map((shop) => (
                  <CommandItem
                    key={shop.id}
                    value={`${shop.name}|${shop.shopifyUrl ?? ""}|${shop.id}`}
                    onSelect={() => {
                      if (shop.id !== currentShopId) {
                        navigate(`/shops/${shop.id}`);
                      }
                      onClose();
                    }}
                    className={cn(
                      "cursor-pointer gap-3 px-3 py-2",
                      currentShopId === shop.id && "bg-accent font-semibold",
                    )}
                  >
                    <StoreIcon className="h-5 w-5 text-muted-foreground" />
                    <span>{shop.name}</span>
                    {shop.shopifyUrl && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        {/* Stored without protocol: `new URL` would throw. */}
                        {shop.shopifyUrl.replace(/^https?:\/\//, "")}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {shouldSearchReturns && limitedReturns.length > 0 && (
              <CommandGroup heading={t("sidebar.returns")}>
                {limitedReturns.map((returnRequest) => (
                  <CommandItem
                    key={returnRequest.id}
                    value={`${returnRequest.orderName}|${returnRequest.orderId}|${returnRequest.id}`}
                    onSelect={() => {
                      if (currentShopId) {
                        navigate(
                          `/shops/${currentShopId}/returns/${returnRequest.id}`,
                        );
                      }
                      onClose();
                    }}
                    className="cursor-pointer gap-3 px-3 py-2"
                  >
                    <PackageIcon className="h-5 w-5 text-muted-foreground" />
                    <div className="flex flex-col gap-1 flex-1">
                      <div className="text-sm font-medium flex gap-2">
                        <div className="">{returnRequest.orderName}</div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {returnRequest.customer.email} •{" "}
                        {returnRequest.line_items.length} item
                        {returnRequest.line_items.length > 1 ? "s" : ""}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {shouldSearchReturns && isLoadingReturns && (
              <CommandGroup heading="Retours">
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="ml-2 text-sm text-muted-foreground">
                    Recherche en cours...
                  </span>
                </div>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
